export const USER_ROLES = {
  CUSTOMER: 'customer',
  SELLER: 'seller',
  ADMIN: 'admin',
};

export const ONBOARDING_STATUS = {
  PENDING: 'pending',
  DOCUMENTS_UPLOADED: 'documents_uploaded',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const BUSINESS_TYPES = [
  { value: 'individual', label: 'Individual/Sole Proprietor' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'pvt_ltd', label: 'Private Limited Company' },
  { value: 'public_ltd', label: 'Public Limited Company' },
  { value: 'llp', label: 'Limited Liability Partnership' },
];

export const DOCUMENT_TYPES = {
  AADHAAR: 'aadhaar',
  PAN: 'pan',
  GST: 'gst',
  TRADE_LICENSE: 'trade_license',
  SHOP_PHOTO: 'shop_photo',
  CANCELLED_CHEQUE: 'cancelled_cheque',
};

export const DOCUMENT_INFO = [
  {
    type: 'aadhaar',
    label: 'Aadhaar Card',
    required: true,
    hasNumber: true,
    numberLabel: 'Aadhaar Number',
    numberPattern: /^\d{12}$/,
    numberPlaceholder: '123456789012',
  },
  {
    type: 'pan',
    label: 'PAN Card',
    required: true,
    hasNumber: true,
    numberLabel: 'PAN Number',
    numberPattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    numberPlaceholder: 'ABCDE1234F',
  },
  {
    type: 'gst',
    label: 'GST Certificate',
    required: false,
    hasNumber: true,
    numberLabel: 'GST Number',
    numberPattern: /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}$/,
    numberPlaceholder: '22ABCDE1234F1Z5',
  },
  {
    type: 'trade_license',
    label: 'Trade License',
    required: false,
    hasNumber: false,
  },
  {
    type: 'shop_photo',
    label: 'Shop Photo',
    required: false,
    hasNumber: false,
  },
  {
    type: 'cancelled_cheque',
    label: 'Cancelled Cheque',
    required: false,
    hasNumber: false,
  },
];

export const ORDER_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
};

export const FULFILLMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  PACKED: 'packed',
  SHIPPED: 'shipped',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
};

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

export const PAYMENT_METHODS = {
  PAYU: 'payu',
  COD: 'cod',
  STRIPE: 'stripe',
  RAZORPAY: 'razorpay',
};

export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024,
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.pdf'],
};

export const OTP = {
  LENGTH: 6,
  EXPIRY_MINUTES: 10,
  RESEND_COOLDOWN: 60,
};

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  ITEMS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
};

export const API_ENDPOINTS = {
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    VALIDATE_RESET_TOKEN: '/auth/validate-reset-token',
    RESET_PASSWORD: '/auth/reset-password',
  },
  
  SELLER_ONBOARDING: {
    REGISTER: '/seller-onboarding/register',
    VERIFY_OTP: '/seller-onboarding/verify-phone-otp',
    RESEND_OTP: '/seller-onboarding/resend-otp',
    STATUS: '/seller-onboarding/status',
    COMPLETE_PROFILE: '/seller-onboarding/complete-profile',
    SUBMIT: '/seller-onboarding/submit',
  },
  
  PRODUCTS: {
    LIST: '/products',
    CREATE: '/products',
    GET: (id) => `/products/${id}`,
    UPDATE: (id) => `/products/${id}`,
    DELETE: (id) => `/products/${id}`,
    BY_SLUG: (slug) => `/products/slug/${slug}`,
  },
  
  CART: {
    GET: '/cart',
    ADD: '/cart',
    UPDATE: (id) => `/cart/${id}`,
    DELETE: (id) => `/cart/${id}`,
    CLEAR: '/cart',
    VALIDATE: '/cart/validate',
  },
  
  WISHLIST: {
    LIST: '/wishlist',
    ADD: '/wishlist',
    REMOVE: (productId) => `/wishlist/${productId}`,
    CHECK: (productId) => `/wishlist/check/${productId}`,
    CLEAR: '/wishlist',
    COUNT: '/wishlist/count',
  },
  
  ORDERS: {
    CREATE: '/orders',
    MY_ORDERS: '/orders/my-orders',
    GET: (id) => `/orders/${id}`,
    CANCEL: (id) => `/orders/${id}/cancel`,
    SELLER_ORDERS: '/orders/seller/orders',
    UPDATE_FULFILLMENT: (id) => `/orders/seller/items/${id}/fulfillment`,
    SELLER_ANALYTICS: '/orders/seller/analytics',
  },
  
  USERS: {
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    CHANGE_PASSWORD: '/users/password',
    ADDRESSES: '/users/addresses',
    ADD_ADDRESS: '/users/addresses',
    UPDATE_ADDRESS: (id) => `/users/addresses/${id}`,
    DELETE_ADDRESS: (id) => `/users/addresses/${id}`,
    SET_DEFAULT: (id) => `/users/addresses/${id}/default`,
    DASHBOARD_STATS: '/users/dashboard-stats',
  },
  
  SELLER: {
    PROFILE: '/sellers/profile',
    UPDATE_PROFILE: '/sellers/profile',
    DASHBOARD: '/sellers/dashboard',
    REVENUE: '/sellers/revenue',
    TOP_PRODUCTS: '/sellers/top-products',
    MY_PRODUCTS: '/sellers/products',
  },
  
  ADMIN: {
    ANALYTICS: '/admin/analytics',
    REVENUE: '/admin/analytics/revenue',
    TOP_SELLERS: '/admin/analytics/top-sellers',
    TOP_PRODUCTS: '/admin/analytics/top-products',
    USERS: '/admin/users',
    PENDING_SELLERS: '/admin/sellers/pending',
    SELLERS_ALL: '/admin/sellers/all',
    GET_SELLER: (id) => `/admin/sellers/${id}`,
    APPROVE_SELLER: (id) => `/admin/sellers/${id}/approve`,
    REJECT_SELLER: (id) => `/admin/sellers/${id}/reject`,
    ALL_ORDERS: '/orders/admin/all',
    UPDATE_ORDER_STATUS: (id) => `/orders/admin/${id}/status`,
    CONTACT_MESSAGES: '/admin/contact-messages',
    MARK_CONTACT_MESSAGE_READ: (id) => `/admin/contact-messages/${id}/read`,
  },

  FOLLOWS: {
    FOLLOW: (sellerId) => `/follows/${sellerId}`,
    UNFOLLOW: (sellerId) => `/follows/${sellerId}`,
    CHECK: (sellerId) => `/follows/check/${sellerId}`,
    FOLLOWING: '/follows/following',
    FOLLOWING_COUNT: '/follows/following/count',
    FOLLOWERS: (sellerId) => `/follows/sellers/${sellerId}/followers`,
    FOLLOWERS_COUNT: (sellerId) => `/follows/sellers/${sellerId}/followers/count`,
  },

  RENTALS: {
    LIST: '/rentals',
    CREATE: '/rentals',
    MY: '/rentals/my/listings',
    GET: (id) => `/rentals/${id}`,
    DELETE: (id) => `/rentals/${id}`,
  },

  BARTER: {
    LIST: '/barter',
    CREATE: '/barter',
    MY: '/barter/my/listings',
    GET: (id) => `/barter/${id}`,
    DELETE: (id) => `/barter/${id}`,
    EXCHANGE_REQUEST: (id) => `/barter/${id}/exchange-request`,
    MY_REQUEST: (id) => `/barter/${id}/my-request`,
    GET_REQUESTS: (id) => `/barter/${id}/exchange-requests`,
    UPDATE_REQUEST_STATUS: (requestId) => `/barter/exchange-requests/${requestId}/status`,
    DELETE_REQUEST: (requestId) => `/barter/exchange-requests/${requestId}`,
  },

  ADS: {
    ACTIVE: '/ads/active',
    CLICK: (id) => `/ads/${id}/click`,
    GIVEAWAY: '/ads/giveaway',
    GIVEAWAY_SETTINGS: '/ads/giveaway/settings',
    ADMIN_ALL: '/ads',
    ADMIN_CREATE: '/ads',
    ADMIN_UPDATE: (id) => `/ads/${id}`,
    ADMIN_DELETE: (id) => `/ads/${id}`,
    ADMIN_TOGGLE: (id) => `/ads/${id}/toggle`,
    ADMIN_GIVEAWAY_ENTRIES: '/ads/giveaway/entries',
    ADMIN_DELETE_GIVEAWAY_ENTRY: (id) => `/ads/giveaway/entries/${id}`,
  },
};

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  CART_COUNT: 'cart_count',
  THEME: 'theme',
};